import React from 'react';
import { TenantData, TenantSearchableResponse } from 'App.types';
import { useTenantContext } from 'contexts/TenantContext/TenantContext';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { useTenantListApi } from 'hooks/useTenantListApi';
import { apiUrl, regionalApiUrl } from 'utils/apiUrl';

export interface JobRegionContextProps {
  /** Whether the job region picker should be shown and used for this tenant. */
  showRegionPicker: boolean;
  /** False while the region is still being auto-detected (Customer tenants only).
   *  Always true for non-Customer tenants. Use to delay fetches until the correct
   *  regional URL is known, avoiding a wasted request to the default endpoint. */
  isJobRegionReady: boolean;
  /** The currently selected job region. Undefined until auto-detection or user selection completes. */
  jobRegion: string | undefined;
  setJobRegion: (region: string) => void;
  jobRegions: string[];
  /** Builds a URL using the regional LCT API base for the active job region if configured,
   *  otherwise falls back to the default reverse-proxied path. */
  lctApiUrl: (path: string, apiVersion?: number) => string;
}

const defaultLctApiUrl = (path: string, apiVersion = 1) => apiUrl('lct', path, apiVersion);

export const JobRegionContext = React.createContext<JobRegionContextProps>({
  showRegionPicker: false,
  isJobRegionReady: true,
  jobRegion: undefined,
  setJobRegion: () => {},
  jobRegions: [],
  lctApiUrl: defaultLctApiUrl,
});

export const useJobRegion = () => React.useContext(JobRegionContext);

const findMostCommonRegion = (children: TenantData[], fallback?: string): string | undefined => {
  const counts: Record<string, number> = {};
  children
    .filter((t) => t.region)
    .forEach((t) => {
      counts[t.region!] = (counts[t.region!] || 0) + 1;
    });
  const sorted = Object.entries(counts).sort(([, a], [, b]) => b - a);
  return sorted.length > 0 ? sorted[0][0] : fallback;
};

export const JobRegionProvider: React.FC = ({ children }) => {
  const { tenant, tenantId } = useTenantContext();
  const env = useAppConfigContext();
  const showRegionPicker = tenant?.tenantType === 'Customer' && !env.cognito?.isIl4;
  const jobRegions = React.useRef(env.tenantSupportedRegions ?? []);
  const [jobRegion, setJobRegion] = React.useState<string | undefined>(() => {
    if (!showRegionPicker) return tenant?.region;
    return undefined;
  });
  const isJobRegionReady = jobRegion !== undefined || (!showRegionPicker && tenant !== undefined);
  const hasAutoDetectedRef = React.useRef(false);

  // Reset when navigating between tenants without unmounting this provider.
  React.useEffect(() => {
    hasAutoDetectedRef.current = false;
    setJobRegion(undefined);
  }, [tenantId]);

  const {
    data: tenantListData,
    fetch: fetchTenantList,
    error,
  } = useTenantListApi<TenantSearchableResponse>(apiUrl('tenancy', 'tenants/searchable'));

  React.useEffect(() => {
    if (error) {
      // Don't show an error UI, but do mark the job region as ready so the app can function without it.
      hasAutoDetectedRef.current = true;
      if (jobRegions.current[0]) setJobRegion(jobRegions.current[0]);
    }
  }, [error]);

  // Auto-select the region based on the most common region of child tenants.
  React.useEffect(() => {
    if (!showRegionPicker) {
      if (tenant?.region) setJobRegion(tenant.region);
      return;
    }

    if (hasAutoDetectedRef.current) return;
    if (!tenantListData) {
      // Not yet in cache — trigger a fetch; effect re-runs when data arrives.
      fetchTenantList().catch(() => {
        hasAutoDetectedRef.current = true;
        if (jobRegions.current[0]) setJobRegion(jobRegions.current[0]);

        return true;
      });
      // setJobRegion('us-east-1');
      // hasAutoDetectedRef.current = true;
      return;
    }

    hasAutoDetectedRef.current = true;

    const children = tenantListData.results.filter((t) => t.parentTenant === tenantId);
    const autoRegion = findMostCommonRegion(children, tenant?.region);
    const defaultRegion =
      autoRegion && jobRegions.current.includes(autoRegion) ? autoRegion : jobRegions.current[0];
    if (defaultRegion) setJobRegion(defaultRegion);
  }, [showRegionPicker, tenant?.region, tenantId, tenantListData, fetchTenantList]);

  const lctApiUrl = React.useCallback(
    (path: string, apiVersion = 1): string => {
      if (showRegionPicker && jobRegion) {
        return regionalApiUrl({
          capabilityName: 'lct',
          region: jobRegion,
          apiPath: path,
          apiVersion,
          stage: env.stage ?? 'local',
        });
      }
      return apiUrl('lct', path, apiVersion);
    },
    [showRegionPicker, jobRegion, env.stage],
  );

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const context: JobRegionContextProps = {
    showRegionPicker,
    isJobRegionReady,
    jobRegion,
    setJobRegion,
    jobRegions: jobRegions.current,
    lctApiUrl,
  };

  return <JobRegionContext.Provider value={context}>{children}</JobRegionContext.Provider>;
};
